import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import ExportButtons from '../components/ExportButtons';

const statusBadge = s => <span className={`badge ${s==='resolved'?'badge-completed':s==='in_progress'?'badge-discussed':'badge-pending'}`}>{s.replace('_',' ')}</span>;
const typeLabel = t => ({ device:'Device', internet:'Internet', ups:'UPS' }[t] || t);
const incidentWhat = i => i.incident_type === 'device' ? `${i.device_name} — ${i.parameter}`
  : ({ internet:'Internet problem', ups:'UPS problem' }[i.incident_type] || i.type_name || i.incident_type);

function Detail({ id, onClose, onChanged, readOnly }) {
  const [inc, setInc] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const [rca, setRca] = useState({ root_cause:'', preventive_action:'', downtime_minutes:'' });
  const [savedRca, setSavedRca] = useState(false);
  const load = useCallback(() => { api.get(`/incidents/${id}`).then(r => { setInc(r.data); setRca({ root_cause:r.data.root_cause||'', preventive_action:r.data.preventive_action||'', downtime_minutes:r.data.downtime_minutes||'' }); }).catch(()=>{}); }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!inc) return null;
  const saveRca = async () => { await api.put(`/incidents/${id}`, rca); setSavedRca(true); setTimeout(()=>setSavedRca(false),1200); };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.post(`/incidents/${id}/note`, { note }); setNote(''); load(); } finally { setBusy(false); }
  };
  const setStatus = async s => { await api.patch(`/incidents/${id}/status`, { status:s }); load(); onChanged && onChanged(); };

  const Row = ({ label, value }) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
      <span style={{ fontSize:12, color:'var(--text2)' }}>{label}</span>
      <span style={{ fontSize:13, fontWeight:500, textAlign:'right' }}>{value || '—'}</span>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:560 }}>
        <div className="modal-header">
          <span className="modal-title">Incident · {incidentWhat(inc)}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ marginBottom:10 }}>{statusBadge(inc.status)}</div>
        <Row label="Campus" value={inc.campus_name} />
        <Row label="Type" value={typeLabel(inc.incident_type)} />
        {inc.incident_type==='device' && <Row label="Device / Parameter" value={`${inc.device_name} — ${inc.parameter}`} />}
        {inc.occurred_at && <Row label="When" value={new Date(inc.occurred_at).toLocaleString()} />}
        {inc.student_name && <Row label="Student" value={`${inc.student_name}${inc.student_grade?` · Grade ${inc.student_grade}${inc.student_section?'-'+inc.student_section:''}`:''}${inc.student_id_number?` · ${inc.student_id_number}`:''}`} />}
        {inc.asset_tag && <Row label="Asset" value={`${inc.asset_tag} · ${inc.asset_brand||''} ${inc.asset_model||''}`} />}
        {inc.room_name && <Row label="Room" value={inc.room_name} />}
        {inc.site_or_proxy && <Row label="Site / Proxy" value={inc.site_or_proxy} />}
        {inc.cctv_data_url && (
          <div style={{ margin:'10px 0' }}>
            <div style={{ fontSize:12, color:'var(--text2)', marginBottom:4 }}>CCTV Screenshot</div>
            <img src={inc.cctv_data_url} alt="CCTV" style={{ maxWidth:'100%', borderRadius:8, border:'1px solid var(--border)' }} />
          </div>
        )}
        <Row label="Reported By" value={inc.reported_by_name} />
        <Row label="Reported At" value={new Date(inc.reported_at).toLocaleString()} />
        <Row label="Vendor" value={inc.vendor_involved ? (inc.vendor_name || 'Yes') : 'No'} />
        <Row label="Linked Task" value={inc.task_id ? <span className="mono" style={{ color:'var(--accent)' }}>{inc.task_id}</span> : '—'} />
        {inc.resolved_at && <Row label="Resolved At" value={new Date(inc.resolved_at).toLocaleString()} />}
        <div style={{ margin:'10px 0' }}>
          <div style={{ fontSize:12, color:'var(--text2)', marginBottom:4 }}>Description</div>
          <div style={{ fontSize:13 }}>{inc.description || '—'}</div>
        </div>

        {/* Root cause / preventive action / downtime */}
        <div style={{ border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:12, margin:'10px 0' }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>Root Cause Analysis</div>
          <div className="form-group"><label>Root Cause</label>
            <textarea rows={2} value={rca.root_cause} disabled={readOnly} onChange={e => setRca(p=>({...p,root_cause:e.target.value}))} /></div>
          <div className="form-group"><label>Preventive Action</label>
            <textarea rows={2} value={rca.preventive_action} disabled={readOnly} onChange={e => setRca(p=>({...p,preventive_action:e.target.value}))} /></div>
          <div className="form-group" style={{ maxWidth:200 }}><label>Downtime (minutes)</label>
            <input type="number" value={rca.downtime_minutes} disabled={readOnly} onChange={e => setRca(p=>({...p,downtime_minutes:e.target.value}))} /></div>
          {!readOnly && <button className="btn btn-secondary btn-sm" onClick={saveRca}>{savedRca?'✓ Saved':'Save RCA'}</button>}
        </div>

        {/* Status controls */}
        {!readOnly && inc.status !== 'resolved' && (
          <div className="flex-gap" style={{ gap:8, marginBottom:12 }}>
            {inc.status === 'open' && <button className="btn btn-secondary btn-sm" onClick={() => setStatus('in_progress')}>Mark In Progress</button>}
            <button className="btn btn-primary btn-sm" onClick={() => setStatus('resolved')}>Mark Resolved</button>
          </div>
        )}

        {/* Notes thread */}
        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em', margin:'6px 0' }}>Follow-up Notes</div>
        {(inc.notes||[]).length === 0 && <div style={{ fontSize:13, color:'var(--text2)' }}>No notes yet.</div>}
        {(inc.notes||[]).map(n => (
          <div key={n.id} style={{ padding:'8px 0', borderTop:'1px solid var(--border)' }}>
            <div style={{ fontSize:13 }}>{n.note}</div>
            <div style={{ fontSize:11, color:'var(--text3)' }}>{n.added_by_name} · {new Date(n.created_at).toLocaleString()}</div>
          </div>
        ))}
        {!readOnly && (
          <div className="flex-gap" style={{ gap:8, marginTop:10 }}>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Add a follow-up note…" style={{ flex:1 }} />
            <button className="btn btn-secondary btn-sm" onClick={addNote} disabled={busy || !note.trim()}>Add</button>
          </div>
        )}
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

const MAX_IMG = 3 * 1024 * 1024;
function AddIncidentModal({ type, campuses, user, onClose, onSaved }) {
  const [form, setForm] = useState({ campus_id: campuses.length===1 ? String(campuses[0].id) : '',
    description:'', student_id:'', asset_id:'', room_id:'', occurred_at:'', site_or_proxy:'' });
  const [students, setStudents] = useState([]);
  const [assets, setAssets] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [cctv, setCctv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const kind = type.kind;

  useEffect(() => {
    if (!form.campus_id) { setStudents([]); setAssets([]); setRooms([]); return; }
    if (kind === 'physical_damage' || kind === 'policy_violation')
      api.get(`/master/students/${form.campus_id}`).then(r => setStudents(r.data.filter(x=>x.active))).catch(()=>{});
    if (kind === 'physical_damage') {
      api.get('/assets', { params:{ campus_id: form.campus_id } }).then(r => setAssets(r.data)).catch(()=>{});
      api.get(`/master/rooms/${form.campus_id}`).then(r => setRooms(r.data.filter(x=>x.active))).catch(()=>{});
    }
  }, [form.campus_id, kind]);

  const onFile = e => {
    const f = e.target.files[0]; if (!f) return;
    if (!/^image\//.test(f.type)) { setErr('Please attach an image'); return; }
    if (f.size > MAX_IMG) { setErr('Image too large (max 3 MB)'); return; }
    const rd = new FileReader(); rd.onload = ev => { setCctv(ev.target.result); setErr(''); }; rd.readAsDataURL(f);
  };

  const save = async () => {
    if (!form.campus_id) { setErr('Select campus'); return; }
    if ((kind==='physical_damage' || kind==='policy_violation') && !form.student_id) { setErr('Select the student'); return; }
    if (kind==='policy_violation' && !form.site_or_proxy.trim()) { setErr('Enter the site / proxy name'); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/incidents', {
        incident_type_id: type.id, campus_id: parseInt(form.campus_id), description: form.description || null,
        student_id: form.student_id || null, asset_id: form.asset_id || null, room_id: form.room_id || null,
        occurred_at: form.occurred_at || null, site_or_proxy: form.site_or_proxy || null,
        cctv_image: kind==='physical_damage' ? cctv : null,
      });
      onSaved();
    } catch(e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:520, maxHeight:'88vh', overflowY:'auto' }}>
        <div className="modal-header"><span className="modal-title">New Incident · {type.name}</span><button className="modal-close" onClick={onClose}>×</button></div>
        {campuses.length > 1 && (
          <div className="form-group"><label>Campus *</label>
            <select value={form.campus_id} onChange={e => setForm(f => ({ ...f, campus_id:e.target.value, student_id:'', asset_id:'', room_id:'' }))}>
              <option value="">— Select —</option>{campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
        )}
        {(kind==='physical_damage' || kind==='policy_violation') && (
          <div className="form-group"><label>Student *</label>
            <select value={form.student_id} onChange={e => setForm(f => ({ ...f, student_id:e.target.value }))}>
              <option value="">— Select student —</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.name}{s.grade?` · ${s.grade}${s.section?'-'+s.section:''}`:''}{s.id_number?` · ${s.id_number}`:''}</option>)}
            </select></div>
        )}
        {kind==='physical_damage' && (<>
          <div className="form-row">
            <div className="form-group"><label>Asset</label>
              <select value={form.asset_id} onChange={e => setForm(f => ({ ...f, asset_id:e.target.value, room_id: (assets.find(a=>String(a.id)===e.target.value)?.room_id)? String(assets.find(a=>String(a.id)===e.target.value).room_id): f.room_id }))}>
                <option value="">— Select asset —</option>
                {assets.map(a => <option key={a.id} value={a.id}>{a.asset_tag} · {a.brand} {a.model}</option>)}
              </select></div>
            <div className="form-group"><label>Room</label>
              <select value={form.room_id} onChange={e => setForm(f => ({ ...f, room_id:e.target.value }))}>
                <option value="">— Select room —</option>{rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select></div>
          </div>
        </>)}
        {kind==='policy_violation' && (
          <div className="form-group"><label>Site / Proxy Name *</label>
            <input value={form.site_or_proxy} onChange={e => setForm(f => ({ ...f, site_or_proxy:e.target.value }))} placeholder="e.g. ultrasurf.us / proxy site" /></div>
        )}
        {kind!=='general' && (
          <div className="form-group"><label>Date &amp; Time</label>
            <input type="datetime-local" value={form.occurred_at} onChange={e => setForm(f => ({ ...f, occurred_at:e.target.value }))} /></div>
        )}
        <div className="form-group"><label>Description</label>
          <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description:e.target.value }))} placeholder="What happened…" /></div>
        {kind==='physical_damage' && (
          <div className="form-group"><label>CCTV Screenshot</label>
            <input type="file" accept="image/*" onChange={onFile} />
            {cctv && <img src={cctv} alt="preview" style={{ maxWidth:'100%', marginTop:8, borderRadius:8, border:'1px solid var(--border)' }} />}
          </div>
        )}
        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Create Incident'}</button>
        </div>
      </div>
    </div>
  );
}

export default function Incidents() {
  const { user, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [filters, setFilters] = useState({ campus_id:'', status:'', type:'' });
  const [detailId, setDetailId] = useState(null);
  const [types, setTypes] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [addType, setAddType] = useState(null);
  const readOnly = ['principal','management','coordinator','requester'].includes(user.role);
  const myCampuses = campuses.filter(c => isAdmin || (user.campus_ids||[]).includes(c.id));

  const load = useCallback(() => {
    const params = {};
    Object.entries(filters).forEach(([k,v]) => { if (v) params[k] = v; });
    api.get('/incidents', { params }).then(r => setRows(r.data)).catch(()=>{});
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/master/campuses').then(r => setCampuses(r.data.filter(c => c.active && (isAdmin || (user.campus_ids||[]).includes(c.id))))).catch(()=>{}); }, [isAdmin, user]);
  useEffect(() => { api.get('/master/incident-types').then(r => setTypes(r.data)).catch(()=>{}); }, []);

  const set = (k,v) => setFilters(f => ({ ...f, [k]:v }));

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', marginBottom:18 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Incidents</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>Infrastructure, physical-damage and policy-violation incidents — with vendor follow-up and escalation.</p>
        </div>
        {!readOnly && (
          <div style={{ position:'relative' }}>
            <button className="btn btn-primary" onClick={() => setMenuOpen(o => !o)}>+ Add Incident ▾</button>
            {menuOpen && (
              <div style={{ position:'absolute', right:0, top:'calc(100% + 4px)', minWidth:220, background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', boxShadow:'0 4px 14px rgba(14,27,71,0.12)', zIndex:1200, padding:4 }}>
                {!types.length ? <div style={{ padding:'8px 12px', fontSize:12, color:'var(--text2)' }}>Add types under Master Data → Incident Types</div>
                : types.map(t => (
                  <button key={t.id} onClick={() => { setAddType(t); setMenuOpen(false); }}
                    style={{ display:'block', width:'100%', textAlign:'left', padding:'8px 12px', fontSize:13, background:'transparent', border:'none', cursor:'pointer', color:'var(--text)', borderRadius:6 }}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--bg3)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    {t.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex-gap" style={{ gap:10, marginBottom:14, flexWrap:'wrap', justifyContent:'space-between' }}>
       <div className="flex-gap" style={{ gap:10, flexWrap:'wrap' }}>
        {campuses.length > 1 && (
          <select value={filters.campus_id} onChange={e => set('campus_id', e.target.value)} style={{ padding:'6px 8px' }}>
            <option value="">All campuses</option>
            {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select value={filters.status} onChange={e => set('status', e.target.value)} style={{ padding:'6px 8px' }}>
          <option value="">All statuses</option>
          <option value="open">Open</option><option value="in_progress">In Progress</option><option value="resolved">Resolved</option>
        </select>
        <select value={filters.type} onChange={e => set('type', e.target.value)} style={{ padding:'6px 8px' }}>
          <option value="">All types</option>
          <option value="device">Device</option><option value="internet">Internet</option><option value="ups">UPS</option>
        </select>
       </div>
       <ExportButtons filename="incidents" title="Incident Logs"
         columns={[{key:'campus_name',label:'Campus'},{key:'incident_type',label:'Type'},{key:'device_name',label:'Device'},{key:'parameter',label:'Parameter'},{key:'description',label:'Description'},{key:'status',label:'Status'},{key:'reported_by_name',label:'Reported By'},{key:'reported_at',label:'Reported At'},{key:'root_cause',label:'Root Cause'},{key:'preventive_action',label:'Preventive Action'},{key:'downtime_minutes',label:'Downtime (min)'},{key:'task_id',label:'Task'}]}
         rows={rows} />
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><h3>No incidents</h3><p>Incidents raised during health checks appear here.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Campus</th><th>Type</th><th>Device / Issue</th><th>Reported</th><th>By</th><th>Status</th><th>Open</th><th>Task</th></tr></thead>
            <tbody>
              {rows.map(i => (
                <tr key={i.id} style={{ cursor:'pointer' }} onClick={() => setDetailId(i.id)}>
                  <td style={{ fontSize:13 }}>{i.campus_name}</td>
                  <td style={{ fontSize:12 }}>{typeLabel(i.incident_type)}</td>
                  <td style={{ fontSize:13 }}>{incidentWhat(i)}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{new Date(i.reported_at).toLocaleString()}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{i.reported_by_name || '—'}</td>
                  <td>{statusBadge(i.status)}</td>
                  <td style={{ fontSize:12, color: i.status!=='resolved' && i.hours_open>=2 ? 'var(--red)':'var(--text2)' }}>{Math.floor(i.hours_open||0)}h</td>
                  <td><span className="mono" style={{ fontSize:11, color:'var(--accent)' }}>{i.task_id || '—'}</span></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {detailId && <Detail id={detailId} readOnly={readOnly} onClose={() => setDetailId(null)} onChanged={load} />}
      {addType && <AddIncidentModal type={addType} campuses={myCampuses} user={user}
        onClose={() => setAddType(null)} onSaved={() => { setAddType(null); load(); }} />}
    </div>
  );
}
